import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../state/AuthContext";

export default function EmployeeFiltersAndKPIs() {
  const { user } = useAuth();
  const email = user?.email || localStorage.getItem("user");
  const [fromMonth, setFromMonth] = useState("");
  const [toMonth, setToMonth] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [filteredKPIs, setFilteredKPIs] = useState({
    totalLeads: 0,
    totalRevenue: 0,
    monthlyLeads: 0,
    monthlyRevenue: 0,
    points: 0,
  });

  // ✅ Restrict selection to last 12 months
  const getMinMonth = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 12);
    return date.toISOString().slice(0, 7);
  };

  const getCurrentMonth = () => new Date().toISOString().slice(0, 7);

  // Fetch analytics from Firestore
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!email) return;
      try {
        const docRef = doc(db, "leadAnalytics", email);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setAnalytics(docSnap.data());
        } else {
          console.warn("No analytics found for", email);
        }
      } catch (err) {
        console.error("Error fetching analytics:", err);
      }
    };

    fetchAnalytics();
  }, [email]);

  // Compute KPI based on selected date range
  useEffect(() => {
    if (!analytics || !fromMonth || !toMonth) return;

    const monthsInRange = getMonthsInRange(fromMonth, toMonth);
    let monthlyLeads = 0;
    let monthlyRevenue = 0;

    monthsInRange.forEach((month) => {
      monthlyLeads += analytics.monthlyleadown?.[month] || 0;
      monthlyRevenue += analytics.monthlyrevenuegenerated?.[month] || 0;
    });

    setFilteredKPIs({
      totalLeads: analytics.totalleadown || 0,
      totalRevenue: analytics.totalrevenuegenerated || 0,
      monthlyLeads,
      monthlyRevenue,
      points: analytics.gamification?.points || 0,
    });
  }, [fromMonth, toMonth, analytics]);

  const getMonthsInRange = (start, end) => {
    const result = [];
    const [startY, startM] = start.split("-").map(Number);
    const [endY, endM] = end.split("-").map(Number);
    const date = new Date(startY, startM - 1);

    while (date.getFullYear() < endY || (date.getFullYear() === endY && date.getMonth() + 1 <= endM)) {
      result.push(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`);
      date.setMonth(date.getMonth() + 1);
    }
    return result;
  };

  return (
    <div className="bg-white rounded-lg shadow p-5 space-y-6">
      {/* Filter Section */}
     <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
  <div>
    <h2 className="text-xl font-semibold text-gray-800">Filter by Month</h2>
    <p className="text-sm text-gray-500">Select range (max 12 months)</p>
  </div>

  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full sm:w-auto">
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700">From</label>
      <input
        type="month"
        min={getMinMonth()}
        max={getCurrentMonth()}
        value={fromMonth}
        onChange={(e) => setFromMonth(e.target.value)}
        className="w-full border border-gray-300 rounded-md p-2 focus:ring-[#6cbb23] focus:border-[#6cbb23]"
      />
    </div>

    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700">To</label>
      <input
        type="month"
        min={fromMonth || getMinMonth()}
        max={getCurrentMonth()}
        value={toMonth}
        onChange={(e) => setToMonth(e.target.value)}
        className="w-full border border-gray-300 rounded-md p-2 focus:ring-[#6cbb23] focus:border-[#6cbb23]"
      />
    </div>
  </div>
</div>


      {/* KPI Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard title="Total Leads Won" value={filteredKPIs.totalLeads} />
        <KpiCard title="Total Revenue Generated" value={`₹${filteredKPIs.totalRevenue.toLocaleString()}`} />
        <KpiCard title="Leads in Range" value={filteredKPIs.monthlyLeads} />
        <KpiCard title="Revenue in Range" value={`₹${filteredKPIs.monthlyRevenue.toLocaleString()}`} />
        <KpiCard title="Reward Points" value={filteredKPIs.points} />
      </div>
    </div>
  );
}

// ✅ Reusable KPI card
const KpiCard = ({ title, value }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow-md transition">
    <h3 className="text-sm font-semibold text-gray-500">{title}</h3>
    <p className="text-2xl font-bold text-[#6cbb23] mt-2">{value}</p>
  </div>
);
